import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
  GraduationCap,
  Smartphone,
  Monitor,
  Building2,
  MessageCircle,
  Target,
  Gift,
  Phone,
  BookOpen,
  CheckCircle2,
  ArrowRight,
  Star,
  Users,
  Clock,
  Rocket,
  ChevronDown,
} from 'lucide-react';

import AnimatedCard from '../components/AnimatedCard';
import SectionHeader from '../components/SectionHeader';
import Seo from '../seo/Seo';
import { COURSES_SEO } from '../seo/pageSeo';

const COURSES = [
  {
    name: 'Full Stack Development',
    tag: 'BESTSELLER',
    color: '#00f5ff',
    desc: 'Master frontend, backend and deployment from scratch.',
    points: [
      'React JS + Next.js',
      'Node.js + Express',
      'MongoDB + SQL',
      'REST APIs + JWT',
      'Cloud Deployment',
      'Real Projects',
    ],
  },
  {
    name: 'Frontend Development',
    tag: 'POPULAR',
    color: '#8b5cf6',
    desc: 'Modern frontend development with responsive design.',
    points: [
      'HTML + CSS + JS',
      'React JS + Hooks',
      'Tailwind CSS',
      'Animations',
      'Portfolio Projects',
    ],
  },
  {
    name: 'Backend Development',
    tag: '',
    color: '#f472b6',
    desc: 'Secure and scalable backend systems & APIs.',
    points: [
      'Node.js',
      'MongoDB',
      'JWT Auth',
      'REST APIs',
      'Server Deployment',
    ],
  },
  {
    name: 'Digital Marketing',
    tag: '',
    color: '#00f5ff',
    desc: 'SEO, Ads and social media growth strategies.',
    points: [
      'SEO',
      'Google Ads',
      'Social Media',
      'Analytics',
      'Email Marketing',
    ],
  },
  {
    name: 'UI/UX Design',
    tag: '',
    color: '#8b5cf6',
    desc: 'Modern UI systems and professional product design.',
    points: [
      'Figma',
      'Wireframes',
      'Prototyping',
      'Design Systems',
      'Case Studies',
    ],
  },
];

const FEATURES = [
  {
    Icon: Monitor,
    text: 'Online Classes',
    color: '#00f5ff',
  },
  {
    Icon: Building2,
    text: 'Offline Training',
    color: '#8b5cf6',
  },
  {
    Icon: MessageCircle,
    text: 'Doubt Support',
    color: '#f472b6',
  },
  {
    Icon: Target,
    text: 'Career Guidance',
    color: '#00f5ff',
  },
  {
    Icon: Gift,
    text: 'Free Demo Classes',
    color: '#8b5cf6',
  },
];

const HOW = [
  {
    step: '01',
    title: 'Book Demo',
    desc: 'Fill the form and reserve your free class.',
    Icon: Smartphone,
    color: '#00f5ff',
  },
  {
    step: '02',
    title: 'Attend Classes',
    desc: 'Join 3 live online or offline demo sessions.',
    Icon: GraduationCap,
    color: '#8b5cf6',
  },
  {
    step: '03',
    title: 'Start Learning',
    desc: 'Enroll only if you are fully satisfied.',
    Icon: Rocket,
    color: '#f472b6',
  },
];

const DOTS = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 5 + 2,
  color: ['#00f5ff','#8b5cf6','#f472b6'][i % 3],
  dur: (Math.random() * 3 + 2).toFixed(1),
  delay: (Math.random() * 4).toFixed(1),
}));

function shuffleArray(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

function InfiniteCourses({ items }) {

  const duplicated = [...items, ...items];

  return (
    <div className="relative overflow-hidden">

      <div className="absolute left-0 top-0 z-20 h-full w-28 bg-gradient-to-r from-[#0a0f1e] to-transparent" />

      <div className="absolute right-0 top-0 z-20 h-full w-28 bg-gradient-to-l from-[#0a0f1e] to-transparent" />

      <div
        className="flex gap-6 w-max"
        style={{
          animation:'infiniteScroll 40s linear infinite'
        }}
      >

        {duplicated.map((c, i) => (
          <article
            key={i}
            className="group relative min-w-[350px] md:min-w-[430px]
            rounded-[2rem] overflow-hidden border border-white/10
            bg-[#0d1428] p-8 transition-all duration-500
            hover:-translate-y-3 hover:border-white/20
            hover:shadow-[0_25px_60px_rgba(0,0,0,.6),0_0_35px_rgba(0,245,255,.16)]"
          >

            {/* glow */}
            <div
              className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500"
              style={{
                background:`radial-gradient(circle,${c.color}25,transparent 70%)`,
                transform:'translate(30%,-30%)'
              }}
            />

            {/* tag */}
            {c.tag && (
              <span
                className="absolute top-5 right-5 px-3 py-1 rounded-full text-xs font-bold"
                style={{
                  background:c.color,
                  color:'#000'
                }}
              >
                {c.tag}
              </span>
            )}

            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
              style={{
                background:`${c.color}15`,
                border:`1px solid ${c.color}35`
              }}
            >
              <BookOpen size={30} color={c.color} />
            </div>

            <h2 className="text-white text-3xl font-bold mb-4">
              {c.name}
            </h2>

            <p className="text-slate-400 leading-relaxed mb-8">
              {c.desc}
            </p>

            <div className="flex flex-col gap-3 mb-8">

              {c.points.map((point, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2
                    size={16}
                    color={c.color}
                    className="mt-1 flex-shrink-0"
                  />

                  <span className="text-slate-300 text-sm">
                    {point}
                  </span>
                </div>
              ))}

            </div>

            <Link
              to="/contact?tab=course"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold no-underline transition-all duration-300 hover:scale-105"
              style={{
                background:`linear-gradient(135deg,${c.color},#8b5cf6)`,
                color:'#fff'
              }}
            >
              Book Free Demo
              <ArrowRight size={18} />
            </Link>

          </article>
        ))}

      </div>

    </div>
  );
}

export default function CoursesPage() {

  const randomCourses = useMemo(() => {
    return shuffleArray(COURSES);
  }, []);

  return (
    <>
      <Seo {...COURSES_SEO} />
      {/* HERO */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden px-8 py-20"
      >

        {/* background */}
        <div className="absolute inset-0">

          <div className="absolute inset-0 bg-[#050810]" />

          <div
            className="absolute -left-60 top-1/3 w-[700px] h-[700px] rounded-full"
            style={{
              background:'radial-gradient(circle,rgba(0,245,255,.13) 0%,transparent 70%)',
              filter:'blur(55px)'
            }}
          />

          <div
            className="absolute -right-60 top-1/2 w-[600px] h-[600px] rounded-full"
            style={{
              background:'radial-gradient(circle,rgba(139,92,246,.15) 0%,transparent 70%)',
              filter:'blur(55px)'
            }}
          />

          <div
            className="absolute left-1/2 bottom-0 -translate-x-1/2 w-[800px] h-[300px]"
            style={{
              background:'radial-gradient(ellipse,rgba(244,114,182,.08) 0%,transparent 70%)',
              filter:'blur(45px)'
            }}
          />

          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:'linear-gradient(rgba(0,245,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,245,255,.03) 1px,transparent 1px)',
              backgroundSize:'50px 50px'
            }}
          />

          {DOTS.map(dot => (
            <span
              key={dot.id}
              className="absolute rounded-full"
              style={{
                left:`${dot.left}%`,
                top:`${dot.top}%`,
                width:`${dot.size}px`,
                height:`${dot.size}px`,
                background:dot.color,
                boxShadow:`0 0 ${dot.size * 4}px ${dot.color}`,
                animation:`dotPulse ${dot.dur}s ease-in-out ${dot.delay}s infinite alternate`,
                opacity:.6
              }}
            />
          ))}

        </div>

        {/* content */}
        <div className="relative z-10 max-w-6xl mx-auto text-center">

          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-400 text-xs tracking-widest mb-8"
          >
            <GraduationCap size={14} />
            FREE 3-DAY DEMO CLASSES
          </div>

          <h1
            className="text-white font-bold leading-tight mb-6"
            style={{
              fontSize:'clamp(2.8rem,7vw,6rem)'
            }}
          >
            Learn Industry <br />
            <span className="grad-text">
              Ready Skills
            </span>
          </h1>

          <p className="text-slate-400 max-w-3xl mx-auto leading-relaxed text-lg mb-12">
            Join premium online & offline training programs with
            real-world projects, modern technologies and expert guidance.
          </p>

          {/* features */}
          <div className="flex flex-wrap justify-center gap-4 mb-14">

            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-5 py-3 rounded-2xl border"
                style={{
                  border:`1px solid ${f.color}25`,
                  background:`${f.color}10`,
                  color:f.color
                }}
              >
                <f.Icon size={16} />
                {f.text}
              </div>
            ))}

          </div>

          {/* buttons */}
          <div className="flex justify-center gap-4 flex-wrap mb-16">

            <Link
              to="/contact?tab=course"
              className="btn-primary no-underline"
            >
              Book Free Demo
            </Link>

            <a
              href="tel:+919499130923"
              className="btn-secondary no-underline"
            >
              <Phone size={16} />
              +91 9499130923
            </a>

          </div>

          {/* stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-5xl mx-auto">

            {[
              ['100+', 'Students'],
              ['25+', 'Projects'],
              ['24/7', 'Support'],
              ['98%', 'Success Rate'],
            ].map(([num, label], i) => (
              <div
                key={i}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
              >
                <h3 className="text-4xl font-bold grad-text mb-2">
                  {num}
                </h3>

                <p className="text-slate-400 text-sm">
                  {label}
                </p>
              </div>
            ))}

          </div>

          <div className="mt-16 flex justify-center">
            <ChevronDown
              size={22}
              color="#00f5ff"
              style={{
                animation:'bounce 1.5s infinite'
              }}
            />
          </div>

        </div>

      </section>

      {/* COURSES */}
      <section className="py-24 bg-[#0a0f1e] overflow-hidden">

        <div className="max-w-7xl mx-auto px-8 mb-14">

          <SectionHeader
            label="OUR COURSES"
            title="Professional Training Programs"
            sub="Premium industry-focused courses with live projects."
          />

        </div>

        <InfiniteCourses items={randomCourses} />

      </section>

      {/* HOW */}
      <section className="py-24 px-8 bg-[#050810]">

        <div className="max-w-7xl mx-auto">

          <SectionHeader
            label="HOW IT WORKS"
            title="Start Learning In 3 Steps"
            sub="Simple process to join your free demo classes."
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">

            {HOW.map((h, i) => (
              <AnimatedCard
                key={i}
                delay={i * 0.1}
                className="relative rounded-[2rem] border border-white/10 bg-[#0d1428] p-8 overflow-hidden"
              >

                <div
                  className="absolute top-4 right-5 text-6xl font-bold opacity-10"
                  style={{
                    color:h.color
                  }}
                >
                  {h.step}
                </div>

                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    background:`${h.color}15`,
                    border:`1px solid ${h.color}30`
                  }}
                >
                  <h.Icon size={30} color={h.color} />
                </div>

                <h3 className="text-white text-2xl font-bold mb-4">
                  {h.title}
                </h3>

                <p className="text-slate-400 leading-relaxed">
                  {h.desc}
                </p>

              </AnimatedCard>
            ))}

          </div>

        </div>

      </section>

      {/* CTA */}
      <section className="px-8 pb-24 bg-[#050810]">

        <div className="max-w-5xl mx-auto">

          <AnimatedCard
            className="rounded-[2rem] border border-cyan-400/10 bg-cyan-400/[0.04] p-12 text-center"
          >

            <h2 className="text-white text-5xl font-bold mb-5">
              Ready To Start Your Journey?
            </h2>

            <p className="text-slate-400 max-w-2xl mx-auto mb-10">
              Book your free demo class today and learn directly from industry developers.
            </p>

            <div className="flex justify-center gap-4 flex-wrap">

              <Link
                to="/contact?tab=course"
                className="btn-primary no-underline"
              >
                <GraduationCap size={18} />
                Book Free Demo
              </Link>

              <Link
                to="/team"
                className="btn-secondary no-underline"
              >
                <Users size={18} />
                Meet The Team
              </Link>

            </div>

          </AnimatedCard>

        </div>

      </section>

      <style>{`

        @keyframes dotPulse {
          from {
            opacity:.25;
            transform:scale(1);
          }
          to {
            opacity:.8;
            transform:scale(1.8);
          }
        }

        @keyframes infiniteScroll {
          from {
            transform:translateX(0);
          }
          to {
            transform:translateX(calc(-50% - 12px));
          }
        }

        @keyframes bounce {
          0%,100% {
            transform:translateY(0);
          }
          50% {
            transform:translateY(10px);
          }
        }

      `}</style>
    </>
  );
}